/**
 * Gesimuleerde detector en scenario-scripts (fase 1 demo + tests).
 * Deze adapter vertaalt "wat er in het beeld gebeurt" naar domeinevents;
 * de echte vision-adapter (fase 3/4) implementeert dezelfde interface.
 */

import type { DomainEvent, PhysicalSide, RallyReason, TeamId } from "../core/types";
import type { DetectionAdapter, DetectionConfig, FieldCalibration } from "./types";

export interface SimStep {
  delayMs?: number;
  event: DomainEvent;
}

export class SimulatedDetectionAdapter implements DetectionAdapter {
  id = "simulated";
  private cfg: DetectionConfig = {
    processWidth: 640,
    targetFps: 15,
    clipAroundEndMs: 8000,
    useVideoFrameCallback: true,
    highConfidence: 0.88,
    mediumConfidence: 0.65,
  };
  private sink: ((ev: DomainEvent) => void) | null = null;
  private timers: number[] = [];

  isSupported(): boolean {
    return true;
  }

  describe(): string {
    return "Gesimuleerde detector (geen camera; scripted events)";
  }

  configure(cfg: Partial<DetectionConfig>): void {
    this.cfg = { ...this.cfg, ...cfg };
  }

  async attach(): Promise<void> {
    /* geen camera nodig */
  }

  start(sink: (ev: DomainEvent) => void): void {
    this.sink = sink;
  }

  stop(): void {
    this.timers.forEach((t) => clearTimeout(t));
    this.timers = [];
    this.sink = null;
  }

  setFieldCalibration(_cal: FieldCalibration | null): void {
    /* simulatie gebruikt de engine-mapping rechtstreeks */
  }

  emit(ev: DomainEvent): void {
    this.sink?.(ev);
  }

  /** Speel een scenario af (voor demo's en handmatige tests op de telefoon). */
  playScenario(steps: SimStep[]): void {
    let t = 0;
    for (const step of steps) {
      t += step.delayMs ?? 400;
      const id = setTimeout(() => this.emit(step.event), t);
      this.timers.push(id);
    }
  }
}

/* ------------------------------------------------------------------ */
/* Scenario-bouwers (alle events hieronder; zie opdracht-minitests)    */
/* ------------------------------------------------------------------ */

let simSeq = 0;
export function nextDetectorId(prefix = "d"): string {
  simSeq += 1;
  return `${prefix}-${simSeq}`;
}

export function serveEv(side: PhysicalSide, confidence: number, detectorId = nextDetectorId("serve")): DomainEvent {
  return { type: "SERVE_DETECTED", detectorId, side, confidence, ts: Date.now() };
}

export function rallyStartEv(detectorId: string): DomainEvent {
  return { type: "RALLY_STARTED", detectorId, ts: Date.now() };
}

export function rallyEndEv(detectorId: string, reason = "ball_down"): DomainEvent {
  return { type: "RALLY_ENDED", detectorId, reason, ts: Date.now() };
}

export function candidateEv(
  detectorId: string,
  winnerTeamId: TeamId | null,
  confidence: number,
  reason: RallyReason = "ball_down_in_court",
  side: PhysicalSide | null = null,
): DomainEvent {
  return {
    type: "RALLY_RESULT_CANDIDATE",
    detectorId,
    winnerTeamId,
    winnerPhysicalSide: side,
    reason,
    confidence,
    evidence: [`sim:conf=${confidence}`],
    ts: Date.now(),
  };
}

export function confirmEv(detectorId?: string, by: "user" | "serve" = "user"): DomainEvent {
  return { type: "RALLY_RESULT_CONFIRMED", confirmedBy: by, ...(detectorId ? { detectorId } : {}), ts: Date.now() };
}

export function manualEv(team: TeamId, detectorId?: string): DomainEvent {
  return { type: "MANUAL_POINT_ASSIGNED", team, ...(detectorId ? { detectorId } : {}), ts: Date.now() };
}

export function sideSwitchEv(): DomainEvent {
  return { type: "SIDE_SWITCH_CONFIRMED", ts: Date.now() };
}

export function undoEv(): DomainEvent {
  return { type: "UNDO_LAST_RALLY", ts: Date.now() };
}
